import React, { useState, useEffect } from "react";
import {
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  withStyles,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import MButton from "@material-ui/core/Button";
import SaveIcon from "@material-ui/icons/Save";
import { Link as LinkComponent } from "@material-ui/core";
import { recordTypes } from "../../../../API/config";
import RecordCheckStatus from "../RecordCheckStatus";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import Icon from "@jll/icon";

const PageHeader = (props) => {
  const [menuAnchorElem, setMenuAnchorElem] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    classes,
    entity,
    hasRecordPermission,
    hasPermission,
    updateRecord,
    allocateCheck,
    finalizeScore,
    approveScore,
    updateEntityConfidentiality,
    sendBlockRequest,
    exportCallback,
    isDirty,
    viewScoreComments,
    handelCancelCheck,
    createAMLCheck,
    onAdditionalSourcesClick,
    handleReferAmlToDealTeam,
    handleReferBackAmlFromDealTeam,
  } = props;
  const { t } = useTranslation();

  const [canUpdate, setCanUpdate] = useState();
  const [canMarkConfidential, setCanMarkConfidential] = useState();
  const [canUnmarkConfidential, setCanUnmarkConfidential] = useState();
  const [canCancel, setCanCancel] = useState();
  const [canRestart, setCanRestart] = useState();
  const [canRequestBlock, setCanRequestBlock] = useState();
  const [canRequestUnblock, setCanRequestUnblock] = useState();
  const [canExport, setCanExport] = useState();
  const [canViewScoreComments, setCanViewScoreComments] = useState();
  const [showMenu, setShowMenu] = useState();
  const [cancelMenuText, setCancelMenuText] = useState();
  const [canAugmentRecord, setCanAugmentRecord] = useState(false);
  const [hasAugmentationSources, setHasAugmentationSources] = useState(false);
  const [canReferAmlToDealTeam, setCanReferAmlToDealTeam] = useState(false);
  const [canReferBackAmlFromDealTeam, setCanReferBackAmlFromDealTeam] =
    useState(false);

  useEffect(() => {
    const setPermissions = async () => {
      const { permissions = {}, checkRequests = [] } = entity || {};
      const canUpdate = hasRecordPermission(
        permissions,
        recordTypes.record.actions.update.name,
      );
      setCanUpdate(canUpdate);
      const canMarkConfidential = hasRecordPermission(
        permissions,
        recordTypes.record.actions.markConfidential.name,
      );
      setCanMarkConfidential(canMarkConfidential);
      const canUnmarkConfidential = hasRecordPermission(
        permissions,
        recordTypes.record.actions.unmarkConfidential.name,
      );
      setCanUnmarkConfidential(canUnmarkConfidential);

      const canRequestBlock = hasRecordPermission(
        permissions,
        recordTypes.record.actions.requestToBlock.name,
      );
      setCanRequestBlock(canRequestBlock);
      const canRequestUnblock = hasRecordPermission(
        permissions,
        recordTypes.record.actions.requestToUnblock.name,
      );
      setCanRequestUnblock(canRequestUnblock);

      const canViewScoreComments = hasRecordPermission(
        permissions,
        recordTypes.record.actions.viewScoreComments.name,
      );

      setCanViewScoreComments(canViewScoreComments);      
      const canExport = hasRecordPermission(
        permissions,
        recordTypes.record.actions.exportRecord.name,
      );

      setCanExport(canExport);

      const canAugmentData = await hasRecordPermission(
        permissions,
        recordTypes.record.actions.augmentData.name,
      );
      setCanAugmentRecord(canAugmentData);

      const kycRequest = checkRequests.find((cr) => cr.check_type === "KYC");
      const amlRequest = checkRequests.find((cr) => cr.check_type === "AML");
      const {
        permissions: kycPermissions,
        process_flow_state: kycState,
        cancelled: kycCancelled,
      } = kycRequest || {};
      const {
        permissions: amlPermissions,
        process_flow_state: amlState,
        cancelled: amlCancelled,
      } = amlRequest || {};
      const kycComplete =
        kycState === "Approved" ||
        kycState === "Proceed At Risk" ||
        kycState === "Rejected" ||
        kycState === "Expired";
      const amlComplete =
        amlState === "Approved" ||
        amlState === "Proceed At Risk" ||
        amlState === "Rejected" ||
        amlState === "Expired";

      const canReferAmlToDealTeam = await hasRecordPermission(
        permissions,
        recordTypes.record.actions.referAmlToDealTeam.name,
      );
      setCanReferAmlToDealTeam(canReferAmlToDealTeam);

      const canReferBackAmlFromDealTeam = await hasRecordPermission(
        permissions,
        recordTypes.record.actions.referBackAmlFromDealTeam.name,
      );
      setCanReferBackAmlFromDealTeam(canReferBackAmlFromDealTeam);

      const canCancel =
        (!kycComplete &&
          !kycCancelled &&
          hasRecordPermission(
            kycPermissions,
            recordTypes.checkRequest.actions.cancel.name,
          )) ||
        (!amlComplete &&
          !amlCancelled &&
          hasRecordPermission(
            amlPermissions,
            recordTypes.checkRequest.actions.cancel.name,
          ));
      const canRestartCancelled =
        (kycCancelled &&
          hasRecordPermission(
            kycPermissions,
            recordTypes.checkRequest.actions.restartCancelled.name,
          )) ||
        (amlCancelled &&
          hasRecordPermission(
            amlPermissions,
            recordTypes.checkRequest.actions.restartCancelled.name,
          ));
      const canRestartCompleted =
        (kycComplete &&
          hasRecordPermission(
            kycPermissions,
            recordTypes.checkRequest.actions.restartCompleted.name,
          )) ||
        (amlComplete &&
          hasRecordPermission(
            amlPermissions,
            recordTypes.checkRequest.actions.restartCompleted.name,
          ));
      const canRestart = canRestartCancelled || canRestartCompleted;
      setCanCancel(canCancel);
      setCanRestart(canRestart);
      setShowMenu(
        canMarkConfidential ||
          canUnmarkConfidential ||
          canRequestBlock ||
          canRequestUnblock ||
          canExport ||
          canCancel ||
          canRestart,
      );

      if (!canCancel && canRestart) {
        setCancelMenuText(t("labels.record.menu.restart"));
      } else if (canCancel && !canRestart) {
        setCancelMenuText(t("labels.record.menu.cancel"));
      } else {
        setCancelMenuText(t("labels.record.menu.cancelRestart"));
      }
    };

    setPermissions();
    setHasAugmentationSources(entity.hasAugmentationSources);
  }, [entity]);

  const openActionMenu = (event) => {
    setMenuAnchorElem(event.currentTarget);
    setIsMenuOpen(Boolean(event.currentTarget));
  };

  const handleMenuItmClick = async (e) => {
    setMenuAnchorElem(null);
    setIsMenuOpen(false);

    if (e && e === "confidential") {
      updateEntityConfidentiality(true);
    } else if (e && e === "unconfidential") {
      updateEntityConfidentiality(false);
    } else if (e && e === "block") {
      sendBlockRequest(true);
    } else if (e && e === "unblock") {
      sendBlockRequest(false);
    } else if (e && e === "export") {
      exportCallback();
    } else if (e && e === "viewScoreComments") {
      viewScoreComments();
    } else if (e && e === "cancel") {
      handelCancelCheck();
    } else if (e && e === "cancel/restart") {
      handelCancelCheck();
    } else if (e && e === "canReferAmlToDealTeam") {
      handleReferAmlToDealTeam();
    } else if (e && e === "canReferBackAmlFromDealTeam") {
      handleReferBackAmlFromDealTeam();
    }
  };

  const showAdditionalSources = () => {
    return (
      <Grid item xs={12} style={{ paddingTop: 0, paddingBottom: 0 }}>
        <LinkComponent
          component="a"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onAdditionalSourcesClick();
          }}
          className={classes.link}
        >
          <Icon className="fa fa-info-circle" style={{ marginRight: 5 }} />
          <Typography component="span" variant="subtitle2">
            {t("labels.record.augmentation.hintText")}
          </Typography>
        </LinkComponent>
      </Grid>
    );
  };

  const { confidential, blocked } = entity || {};

  return (
    <div className={classes.pageHeader}>
      <Grid container direction="row" alignItems="center" spacing={4}>
        <Grid item md={5}>
          <RecordCheckStatus
            checkType="KYC"
            entity={entity}
            allocateCheck={allocateCheck}
            finalizeScore={finalizeScore}
            approveScore={approveScore}
            hasRecordPermission={hasRecordPermission}
            hasPermission={hasPermission}
            createAMLCheck={createAMLCheck}
          />
        </Grid>
        <Grid item md={5}>
          <RecordCheckStatus
            checkType="AML"
            entity={entity}
            allocateCheck={allocateCheck}
            finalizeScore={finalizeScore}
            approveScore={approveScore}
            hasRecordPermission={hasRecordPermission}
            hasPermission={hasPermission}
            createAMLCheck={createAMLCheck}
          ></RecordCheckStatus>
        </Grid>
        <Grid item md={2} container justifyContent="flex-end">
          {canUpdate && (
            <MButton
              color="primary"
              variant="contained"
              label={t("labels.common.save")}
              onClick={(e) => updateRecord()}
              style={{ height: 40 }}
              disabled={!isDirty}
            >
              <SaveIcon />
            </MButton>
          )}
          {showMenu && (
            <>
              <IconButton
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={openActionMenu}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="long-menu"
                anchorEl={menuAnchorElem}
                keepMounted
                open={isMenuOpen}
                disableScrollLock={true}
                onClose={handleMenuItmClick}
              >
                {canReferAmlToDealTeam && (
                  <MenuItem
                    key="canReferAmlToDealTeam"
                    onClick={() => handleMenuItmClick("canReferAmlToDealTeam")}
                  >
                    {t("labels.record.menu.referToDealTeam")}
                  </MenuItem>
                )}
                {canReferBackAmlFromDealTeam && (
                  <MenuItem
                    key="canReferBackAmlFromDealTeam"
                    onClick={() =>
                      handleMenuItmClick("canReferBackAmlFromDealTeam")
                    }
                  >
                    {t("labels.record.menu.referToAMLTeam")}
                  </MenuItem>
                )}

                {canMarkConfidential && !confidential && (
                  <MenuItem
                    key="confidential"
                    onClick={() => handleMenuItmClick("confidential")}
                  >
                    {t("labels.record.menu.markConfidential")}
                  </MenuItem>
                )}

                {canUnmarkConfidential && confidential && (
                  <MenuItem
                    key="unconfidential"
                    onClick={() => handleMenuItmClick("unconfidential")}
                  >
                    {t("labels.record.menu.unmarkConfidential")}
                  </MenuItem>
                )}

                {canRequestBlock && !blocked && (
                  <MenuItem
                    key="unblock"
                    onClick={() => handleMenuItmClick("unblock")}
                  >
                    {t("labels.record.menu.requestToBlock")}
                  </MenuItem>
                )}

                {canRequestUnblock && blocked && (
                  <MenuItem
                    key="Block"
                    onClick={() => handleMenuItmClick("block")}
                  >
                    {t("labels.record.menu.requestToUnBlock")}
                  </MenuItem>
                )}
                {canExport && (
                  <MenuItem
                    key="export"
                    onClick={() => handleMenuItmClick("export")}
                  >
                    {t("labels.record.menu.exportReport")}
                  </MenuItem>
                )}

                {canViewScoreComments && (
                  <MenuItem
                    key="canViewScoreComments"
                    onClick={() => handleMenuItmClick("viewScoreComments")}
                  >
                    {t("labels.record.menu.viewScoreComments")}
                  </MenuItem>
                )}
                {(canCancel || canRestart) && (
                  <MenuItem
                    key="cancel"
                    onClick={() => handleMenuItmClick("cancel/restart")}
                  >
                    {cancelMenuText}
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Grid>
        {canUpdate &&
          canAugmentRecord &&
          hasAugmentationSources &&
          showAdditionalSources()}
      </Grid>
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(PageHeader);
